import { fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    NgZone,
    ViewChild
} from '@angular/core';
import { ResizeElementService } from '@core/services/resize-element.service';
import { closest, pagesToPagePositions } from '@features/slider/slider/slider.functions';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { scrollContainer } from '@shared/scroll/scroll-container';
import { NgClass, NgStyle } from '@angular/common';

const SLIDER_RESIZE_KEY = 'sliderResizeKey';

@UntilDestroy()
@Component({
    selector: 'app-slider',
    templateUrl: './slider.component.html',
    styleUrls: ['./slider.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, NgStyle]
})
export class SliderComponent implements AfterViewInit {
    public pages: number[] = [];
    public activePage: number;
    public hasPaging: boolean;
    public pagingLeftPossible: boolean;
    public pagingRightPossible: boolean;

    /**
     * Add amounts of items to scroll when using arrows.
     * Defaults to container width
     */
    @Input()
    private readonly scrollByItems: number | null = null;

    @ViewChild('content')
    private readonly content: ElementRef<HTMLDivElement>;

    @Input()
    public style: Partial<CSSStyleDeclaration & { scrollPaddingLeft: string; scrollPaddingRight: string }>;

    @Input()
    public readonly centerAlign?: boolean;

    /**
     * Will move to the correct page when it's ready
     */
    @Input()
    public readonly pageIndex: number = 0;

    constructor(
        private resizeElementService: ResizeElementService,
        private ngZone: NgZone,
        private cd: ChangeDetectorRef
    ) {}

    ngAfterViewInit(): void {
        this.resizeElementService
            .observe(SLIDER_RESIZE_KEY, this.content.nativeElement)
            .pipe(debounceTime(100), untilDestroyed(this))
            .subscribe(() => {
                this.setPaging();
                if (this.activePage !== undefined) {
                    this.goToPage(this.pages[this.activePage]);
                }

                this.setCurrentPage();
            });

        fromEvent(this.content.nativeElement, 'scroll')
            .pipe(debounceTime(100), untilDestroyed(this))
            .subscribe(() => this.setActivePage());

        this.setPaging();
    }

    public next = (): void => this.setAmountToScroll(true);
    public previous = (): void => this.setAmountToScroll(false);

    public goToPage(pagePosition: number, index?: number): void {
        scrollContainer({ left: pagePosition }, this.content.nativeElement);
        if (index !== undefined) {
            this.setActivePageInstant(index);
        }
    }

    public trackByPage(_: number, page: number): string {
        return `id-${page}`;
    }

    // Updates the amount of pages in slider. Sets the active page
    public setPaging(): void {
        const { childNodes, offsetWidth, scrollWidth } = this.content.nativeElement;
        const children = this.getOnlyChildNodes(childNodes);
        const firstChild = children[0] as HTMLElement;

        if (!firstChild?.offsetWidth) {
            return;
        }

        const childrenTotalWidth = children.length ? firstChild.offsetWidth * children.length : null;

        if (!childrenTotalWidth) {
            return;
        }

        const pageAmount = Math.ceil(childrenTotalWidth / offsetWidth);

        // ResizeObserver event is not monkey patched by Angular,
        // so we do not have the callback it in our zone, Do a ngZone.run to reenter zone.
        this.ngZone.run(() => {
            this.pages = pagesToPagePositions(pageAmount, offsetWidth);
            this.hasPaging = Boolean(offsetWidth < scrollWidth);
            this.setActivePage();
        });

        // Trigger Changedetection since this is an async callback
        this.cd.markForCheck();
    }

    private setActivePageInstant(index: number): void {
        this.activePage = index;
    }

    private setActivePage(): void {
        const { scrollLeft, scrollWidth, offsetWidth } = this.content.nativeElement;
        const atScrollEnd = scrollLeft + offsetWidth === scrollWidth;

        this.pagingLeftPossible = scrollLeft > 0;
        this.pagingRightPossible = !atScrollEnd;

        // In the case that the user has scrolled to the end of the slider
        // We do not want to set closest but last
        this.activePage = atScrollEnd ? this.pages.length - 1 : this.pages.indexOf(closest(this.pages, scrollLeft));

        // Trigger Changedetection since this is an async callback
        this.cd.markForCheck();
    }

    private setAmountToScroll(slideRight: boolean): void {
        const { scrollLeft, offsetWidth, childNodes } = this.content.nativeElement;
        // Strip Angular comments
        const children = Array.from(childNodes).filter(node => !(node instanceof Comment));

        const scrollByItems = this.scrollByItems;
        let amountToScroll = offsetWidth;

        if (scrollByItems && children.length) {
            amountToScroll = (children[0] as HTMLElement).offsetWidth * scrollByItems;
        }

        scrollContainer(
            {
                left: slideRight ? scrollLeft + amountToScroll : scrollLeft - amountToScroll
            },
            this.content.nativeElement
        );
    }

    // this is only used if we need to go to a specific item in the list
    private setCurrentPage(): void {
        if (!this.pageIndex || this.pageIndex === -1) {
            return;
        }

        const { childNodes, offsetWidth } = this.content.nativeElement;
        const children = this.getOnlyChildNodes(childNodes);
        const currentNode = children[this.pageIndex] as HTMLElement;

        if (!currentNode) {
            return;
        }

        let center = 0;
        if (this.centerAlign) {
            // get the center of the available area to center the scroll position
            center = (offsetWidth - currentNode.offsetWidth) / 2;
        }
        const scrollPosition = currentNode.offsetLeft - center;

        this.goToPage(scrollPosition);
    }

    private getOnlyChildNodes(childNodes: NodeList): HTMLElement[] {
        // Strip Angular comments
        return Array.from(childNodes).filter(node => !(node instanceof Comment)) as HTMLElement[];
    }
}
